<template>
  <AppLayout 
    page-title="Settings" 
    page-subtitle="Manage your account settings and preferences"
  >
    <!-- Settings Content -->
    <div class="space-y-8 pt-8">
      <!-- Settings Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Account Settings -->
        <div class="dashboard-card">
          <div class="p-6 border-b border-slate-700/50">
            <h3 class="text-xl font-bold text-white mb-2">Account Settings</h3>
            <p class="text-slate-400">Manage your basic account information</p>
          </div>
          <div class="p-6 space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
              <input 
                v-model="accountSettings.email" 
                type="email" 
                class="form-input" 
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="your@email.com"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
              <input 
                v-model="accountSettings.fullName" 
                type="text" 
                class="form-input"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="John Doe"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
              <input 
                v-model="accountSettings.phone" 
                type="tel" 
                class="form-input"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="+1 (555) 123-4567"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Job Title</label>
              <input 
                v-model="accountSettings.jobTitle" 
                type="text" 
                class="form-input"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="Oilfield Worker"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Company</label>
              <input 
                v-model="accountSettings.company" 
                type="text" 
                class="form-input"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="Your Company"
              >
            </div>
            <div class="pt-4">
              <button 
                @click="saveAccountSettings"
                :disabled="isTestProfile"
                class="btn-primary-modern w-full"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>

        <!-- Security Settings -->
        <div class="dashboard-card">
          <div class="p-6 border-b border-slate-700/50">
            <h3 class="text-xl font-bold text-white mb-2">Security Settings</h3>
            <p class="text-slate-400">Manage your password and security preferences</p>
          </div>
          <div class="p-6 space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Current Password</label>
              <input 
                v-model="securitySettings.currentPassword" 
                type="password" 
                class="form-input"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="Enter current password"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">New Password</label>
              <input 
                v-model="securitySettings.newPassword" 
                type="password" 
                class="form-input"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="Enter new password"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Confirm New Password</label>
              <input 
                v-model="securitySettings.confirmPassword" 
                type="password" 
                class="form-input"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
                :disabled="isTestProfile"
                placeholder="Confirm new password"
              >
            </div>
            <div class="pt-4">
              <button 
                @click="changePassword"
                :disabled="isTestProfile"
                class="btn-primary-modern w-full"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>

        <!-- Notification Settings -->
        <div class="dashboard-card">
          <div class="p-6 border-b border-slate-700/50">
            <h3 class="text-xl font-bold text-white mb-2">Notification Settings</h3>
            <p class="text-slate-400">Customize your notification preferences</p>
          </div>
          <div class="p-6 space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-white">Email Notifications</h4>
                <p class="text-xs text-slate-400">Receive notifications via email</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  v-model="notificationSettings.emailNotifications" 
                  type="checkbox" 
                  class="sr-only peer"
                  :disabled="isTestProfile"
                >
                <div class="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-white">SMS Notifications</h4>
                <p class="text-xs text-slate-400">Receive notifications via SMS</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  v-model="notificationSettings.smsNotifications" 
                  type="checkbox" 
                  class="sr-only peer"
                  :disabled="isTestProfile"
                >
                <div class="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-white">Job Alerts</h4>
                <p class="text-xs text-slate-400">Get notified about new job opportunities</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  v-model="notificationSettings.jobAlerts" 
                  type="checkbox" 
                  class="sr-only peer"
                  :disabled="isTestProfile"
                >
                <div class="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-white">Safety Updates</h4>
                <p class="text-xs text-slate-400">Receive important safety notifications</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  v-model="notificationSettings.safetyUpdates" 
                  type="checkbox" 
                  class="sr-only peer"
                  :disabled="isTestProfile"
                >
                <div class="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>
            <div class="pt-4">
              <button 
                @click="saveNotificationSettings"
                :disabled="isTestProfile"
                class="btn-primary-modern w-full"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>

        <!-- Privacy Settings -->
        <div class="dashboard-card">
          <div class="p-6 border-b border-slate-700/50">
            <h3 class="text-xl font-bold text-white mb-2">Privacy Settings</h3>
            <p class="text-slate-400">Control your privacy and data preferences</p>
          </div>
          <div class="p-6 space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-white">Profile Visibility</h4>
                <p class="text-xs text-slate-400">Allow other users to see your profile</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  v-model="privacySettings.profileVisibility" 
                  type="checkbox" 
                  class="sr-only peer"
                  :disabled="isTestProfile"
                >
                <div class="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-medium text-white">Data Analytics</h4>
                <p class="text-xs text-slate-400">Allow us to collect usage data</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  v-model="privacySettings.dataAnalytics" 
                  type="checkbox" 
                  class="sr-only peer"
                  :disabled="isTestProfile"
                >
                <div class="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>
            <div class="pt-4">
              <button 
                @click="savePrivacySettings"
                :disabled="isTestProfile"
                class="btn-primary-modern w-full"
                :class="{ 'opacity-50 cursor-not-allowed': isTestProfile }"
              >
                Save Privacy Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Danger Zone -->
      <div class="dashboard-card border-red-500/20">
        <div class="p-6 border-b border-slate-700/50">
          <h3 class="text-xl font-bold text-red-400 mb-2">Danger Zone</h3>
          <p class="text-slate-400">Irreversible and destructive actions</p>
        </div>
        <div class="p-6 space-y-4">
          <div class="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div>
              <h4 class="text-sm font-medium text-red-400">Delete Account</h4>
              <p class="text-xs text-slate-400">Permanently delete your account and personal data. You can also use the public <router-link to="/data-deletion" class="text-red-300 underline">data deletion request</router-link>.</p>
            </div>
            <button 
              @click="showDeleteAccountModal = true"
              class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Account Modal -->
    <div v-if="showDeleteAccountModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div class="bg-slate-800 border border-slate-700/50 rounded-xl p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-bold text-white mb-4">Delete Account</h3>
        <p class="text-slate-400 mb-6">This action cannot be undone. All your data will be permanently deleted.</p>
        <div class="space-y-4">
          <input 
            v-model="deleteConfirmation" 
            type="text" 
            class="form-input" 
            placeholder="Type 'DELETE' to confirm"
          >
          <div class="flex space-x-3">
            <button 
              @click="confirmDeleteAccount"
              :disabled="deleteConfirmation !== 'DELETE'"
              class="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              Delete Account
            </button>
            <button 
              @click="showDeleteAccountModal = false"
              class="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const router = useRouter()
const userStore = useUserStore()

// Reactive data
const showDeleteAccountModal = ref(false)
const deleteConfirmation = ref('')

// Settings objects
const accountSettings = reactive({
  email: '',
  fullName: '',
  phone: '',
  jobTitle: '',
  company: ''
})

const securitySettings = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const notificationSettings = reactive({
  emailNotifications: true,
  smsNotifications: false,
  jobAlerts: true,
  safetyUpdates: true
})

const privacySettings = reactive({
  profileVisibility: true,
  dataAnalytics: true
})

// Computed properties
const isTestProfile = computed(() => {
  return userStore.currentUser?.email === 'test@example.com'
})

// Methods
const saveAccountSettings = async () => {
  try {
    const token = localStorage.getItem('auth_token')
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
    const payloads = [
      { settings_type: 'account_settings', settings_data: accountSettings },
      {
        settings_type: 'notification_settings',
        settings_data: {
          email_notifications: notificationSettings.emailNotifications,
          sms_notifications: notificationSettings.smsNotifications,
          job_alerts: notificationSettings.jobAlerts,
          safety_alerts: notificationSettings.safetyUpdates,
          payment_updates: true,
          push_notifications: true
        }
      },
      {
        settings_type: 'privacy_settings',
        settings_data: {
          profile_visibility: privacySettings.profileVisibility ? 'public' : 'private',
          analytics: privacySettings.dataAnalytics,
          data_sharing: false,
          location_sharing: false,
          contact_info_visible: true
        }
      }
    ]
    for (const payload of payloads) {
      const response = await fetch('/api/user/update-settings.php', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      })
      if (!response.ok) throw new Error('Failed to save settings')
    }
    alert('Settings saved')
  } catch (error) {
    console.error('Error saving account settings:', error)
    alert('Could not save settings')
  }
}

const changePassword = async () => {
  if (securitySettings.newPassword !== securitySettings.confirmPassword) {
    alert('Passwords do not match')
    return
  }
  
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    console.log('Password changed successfully')
    securitySettings.currentPassword = ''
    securitySettings.newPassword = ''
    securitySettings.confirmPassword = ''
    // Show success notification
  } catch (error) {
    console.error('Error changing password:', error)
  }
}

const saveNotificationSettings = async () => {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    console.log('Notification settings saved:', notificationSettings)
    // Show success notification
  } catch (error) {
    console.error('Error saving notification settings:', error)
  }
}

const savePrivacySettings = async () => {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    console.log('Privacy settings saved:', privacySettings)
    // Show success notification
  } catch (error) {
    console.error('Error saving privacy settings:', error)
  }
}

const confirmDeleteAccount = async () => {
  if (deleteConfirmation.value !== 'DELETE') {
    return
  }
  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch('/api/privacy/delete-request.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        email: userStore.currentUser?.email || accountSettings.email,
        fullName: userStore.currentUser?.name || accountSettings.fullName,
        accountType: userStore.currentUser?.userType || '',
        requestType: 'account',
        confirmation: 'DELETE',
        details: 'Submitted from Settings → Delete Account',
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || 'Deletion request failed')
    if (data.demoBlocked) {
      alert(data.message)
      return
    }
    userStore.logout()
    router.push('/data-deletion')
  } catch (error) {
    console.error('Error deleting account:', error)
    alert('Could not complete deletion. Use /data-deletion or email contact@oilbuddy.ai.')
  }
}

// Lifecycle
onMounted(() => {
  // Load user data
  const user = userStore.currentUser
  if (user) {
    accountSettings.email = user.email || ''
    accountSettings.fullName = user.name || ''
    accountSettings.phone = user.phone || ''
    accountSettings.jobTitle = user.jobTitle || ''
    accountSettings.company = user.company || ''
  }
})
</script>

<style scoped>
.dashboard-card {
  @apply bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-colors;
}

.btn-primary-modern {
  @apply px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}
</style>
